import { OnboardingTabConfig } from '@/lib/dtos';

export interface PatchTenantCreationRequestData {
  appName?: string;
  appContactEmail?: string;
  storeName?: string;
  storeUrl?: string;
  contactPhone?: string;
  storePlatform?: string;
  storeFtpHost?: string;
  storeFtpUsername?: string;
  storeFtpPassword?: string;
  storePrivacyPolicyUrl?: string;
  appIcon?: File;
  appIconBackgroundColor?: string;
  splashScreen?: File;
  splashScreenBackgroundColor?: string;
  statusBarTheme?: 'light' | 'dark';

  websiteBannerEnabled?: boolean;
  websiteBannerText?: string;
  websiteBannerButtonText?: string;
  websiteBannerBackgroundColor?: string;
  websiteBannerForegroundColor?: string;

  shopifyCollaboratorAccessCode?: string;

  tabs?: OnboardingTabConfig[];
  accentColor?: string;
  notificationPreviewTitle?: string;
  notificationPreviewBody?: string;
}

/**
 * Serializes a partial tenant-creation-request update into the multipart body
 * the backend's PATCH endpoint expects (it shares the body with the icon/splash
 * file uploads, so every value travels as a string — `tabs` as JSON).
 *
 * A field left `undefined` is omitted entirely, which the backend reads as
 * "keep what's saved"; an explicit empty string clears it. A field missing
 * from this function is silently never sent, so any new field on the DTO
 * needs a line here (see the spec).
 */
export function buildPatchTenantCreationRequestFormData(
  data: PatchTenantCreationRequestData
): FormData {
  const formData = new FormData();
  if (data.appName !== undefined) {
    formData.append('appName', data.appName);
  }
  if (data.appContactEmail !== undefined) {
    formData.append('appContactEmail', data.appContactEmail);
  }
  if (data.storeName !== undefined) {
    formData.append('storeName', data.storeName);
  }
  if (data.storeUrl !== undefined) {
    formData.append('storeUrl', data.storeUrl);
  }
  if (data.contactPhone !== undefined) {
    formData.append('contactPhone', data.contactPhone);
  }
  if (data.storePlatform !== undefined) {
    formData.append('storePlatform', data.storePlatform);
  }
  if (data.storeFtpHost !== undefined) {
    formData.append('storeFtpHost', data.storeFtpHost);
  }
  if (data.storeFtpUsername !== undefined) {
    formData.append('storeFtpUsername', data.storeFtpUsername);
  }
  if (data.storeFtpPassword !== undefined) {
    formData.append('storeFtpPassword', data.storeFtpPassword);
  }
  if (data.storePrivacyPolicyUrl !== undefined) {
    formData.append('storePrivacyPolicyUrl', data.storePrivacyPolicyUrl);
  }
  if (data.appIcon) {
    formData.append('appIcon', data.appIcon);
  }
  if (data.appIconBackgroundColor !== undefined) {
    formData.append('appIconBackgroundColor', data.appIconBackgroundColor);
  }
  if (data.splashScreen) {
    formData.append('splashScreen', data.splashScreen);
  }
  if (data.splashScreenBackgroundColor !== undefined) {
    formData.append(
      'splashScreenBackgroundColor',
      data.splashScreenBackgroundColor
    );
  }
  if (data.statusBarTheme !== undefined) {
    formData.append('statusBarTheme', data.statusBarTheme);
  }
  if (data.shopifyCollaboratorAccessCode !== undefined) {
    formData.append(
      'shopifyCollaboratorAccessCode',
      data.shopifyCollaboratorAccessCode
    );
  }
  if (data.tabs !== undefined) {
    formData.append('tabs', JSON.stringify(data.tabs));
  }
  if (data.accentColor !== undefined) {
    formData.append('accentColor', data.accentColor);
  }
  if (data.notificationPreviewTitle !== undefined) {
    formData.append('notificationPreviewTitle', data.notificationPreviewTitle);
  }
  if (data.notificationPreviewBody !== undefined) {
    formData.append('notificationPreviewBody', data.notificationPreviewBody);
  }
  if (data.websiteBannerEnabled !== undefined) {
    formData.append('websiteBannerEnabled', String(data.websiteBannerEnabled));
    if (data.websiteBannerText !== undefined) {
      formData.append('websiteBannerText', data.websiteBannerText);
    }
    if (data.websiteBannerButtonText !== undefined) {
      formData.append('websiteBannerButtonText', data.websiteBannerButtonText);
    }
    if (data.websiteBannerBackgroundColor !== undefined) {
      formData.append(
        'websiteBannerBackgroundColor',
        data.websiteBannerBackgroundColor
      );
    }
    if (data.websiteBannerForegroundColor !== undefined) {
      formData.append(
        'websiteBannerForegroundColor',
        data.websiteBannerForegroundColor
      );
    }
  }

  return formData;
}
